import { useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { loadDirectMetricsAsync } from '../../../../data-layer/direct/actions';
import { selectMetrics } from '../../../../data-layer/direct/selectors';
import {
    DirectCampaignUI,
    DirectGoalCostType,
    DirectPromotionGoalUI,
} from '../../../../data-layer/direct/types';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';

function getMetricsSettings(company?: DirectCampaignUI) {
    const { counters, weeklyLimit } = company || {};

    return {
        selectedCounters: counters?.map((counter) => String(counter.counter_id)) ?? [],
        selectedGoals:
            counters?.reduce<DirectPromotionGoalUI>((res, counter) => {
                const selected = counter.goals.reduce<DirectPromotionGoalUI>(
                    (goals, goal) => ({
                        ...goals,
                        [String(goal.goal_id)]: {
                            cost: goal.cost,
                            costType:
                                goal.cost || !goal.is_metrika_source_of_value
                                    ? DirectGoalCostType.MANUAL
                                    : DirectGoalCostType.DYNAMIC,
                        },
                    }),
                    {},
                );

                return {
                    ...res,
                    ...selected,
                };
            }, {}) ?? {},
        weeklyLimit,
    };
}

export const usePromotionMetrics = (company?: DirectCampaignUI) => {
    const dispatch = useDispatch();
    const metrics = useSelector((state) => selectMetrics(state));
    const onLoadMetrics = useCallback(() => dispatch(loadDirectMetricsAsync()), [dispatch]);

    const metricsSettings = getMetricsSettings(company);

    const [selectedCounters, setSelectedCounters] = useState<string[]>(
        metricsSettings.selectedCounters,
    );
    const onSelectCounter = useCallback(
        (key: string) => {
            if (selectedCounters.indexOf(key) !== -1) {
                return;
            }

            setSelectedCounters([...selectedCounters, key]);
        },
        [selectedCounters],
    );
    const onRemoveCounter = useCallback(
        (key: string) => {
            const newSelectedMetrics = selectedCounters.filter((seleced) => seleced !== key);

            setSelectedCounters(newSelectedMetrics);
        },
        [selectedCounters],
    );

    const [selectedGoals, setSelectedGoals] = useState<DirectPromotionGoalUI>(
        metricsSettings.selectedGoals,
    );
    const onSelectGoals = useCallback(
        (selected: string[]) => {
            const newSelectedGoals = selected.reduce(
                (res, id) => ({
                    ...res,
                    [id]: selectedGoals?.[id] || { costType: DirectGoalCostType.DYNAMIC },
                }),
                {},
            );

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );
    const onRemoveGoal = useCallback(
        (key: string) => {
            const newSelectedGoals = { ...selectedGoals };
            delete newSelectedGoals[key];

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );
    const onChangeGoalBudget = useCallback(
        (key: string, value: string) => {
            const costValue = Number(value);
            const newSelectedGoals = {
                ...selectedGoals,
                [key]: {
                    cost: costValue,
                    costType: costValue ? DirectGoalCostType.MANUAL : selectedGoals[key].costType,
                },
            };

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );

    const [weeklyLimit, setWeeklyLimit] = useState<number | undefined>(metricsSettings.weeklyLimit);
    const onChangeWeeklyLimit = useCallback((value: string) => {
        setWeeklyLimit(Number(value));
    }, []);

    const onChangeGoalCostType = useCallback(
        (key: string, value: DirectGoalCostType) => {
            const newSelectedGoals = {
                ...selectedGoals,
                [key]: {
                    cost: selectedGoals[key].cost,
                    costType: value,
                },
            };

            setSelectedGoals(newSelectedGoals);
        },
        [selectedGoals],
    );

    useEffectOnce(() => {
        onLoadMetrics();
    });

    return {
        metrics,
        selectedCounters,
        onSelectCounter,
        onRemoveCounter,
        selectedGoals,
        onSelectGoals,
        onRemoveGoal,
        onChangeGoalBudget,
        weeklyLimit,
        onChangeWeeklyLimit,
        onChangeGoalCostType,
    };
};
